import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { auth, db, functions, storage } from "./firebase";
import type { JobDoc, JobWithId } from "../types/job";
import type {
  CreateJobRequest,
  CreateJobResponse,
  DeleteJobRequest,
  DeleteJobResponse,
  DescribeImageRequest,
  DescribeImageResponse,
} from "../types/requests";

const downloadUrlCache = new Map<string, Promise<string>>();

function extensionFromType(type: string): string {
  if (type === "image/png") return "png";
  if (type === "image/webp") return "webp";
  return "jpg";
}

export async function uploadInputs(files: File[], uid: string, jobId: string): Promise<string[]> {
  const paths: string[] = [];

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const ext = extensionFromType(file.type);
    const path = `users/${uid}/jobs/${jobId}/inputs/input${i + 1}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    paths.push(path);
  }

  return paths;
}

export async function uploadInput(
  file: Blob,
  uid: string,
  jobId: string,
  index: number,
): Promise<string> {
  const type = file.type || "image/jpeg";
  const ext = extensionFromType(type);
  const path = `users/${uid}/jobs/${jobId}/inputs/input${index + 1}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: type });
  return path;
}

export async function deleteStoragePath(path: string): Promise<void> {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}

export async function createJob(payload: CreateJobRequest): Promise<string> {
  const callable = httpsCallable<CreateJobRequest, CreateJobResponse>(functions, "createJob");
  const result = await callable(payload);
  return result.data.jobId;
}

export async function deleteJob(jobId: string): Promise<void> {
  const callable = httpsCallable<DeleteJobRequest, DeleteJobResponse>(functions, "deleteJob");
  await callable({ jobId });
}

export async function describeImage(path: string): Promise<string> {
  const callable = httpsCallable<DescribeImageRequest, DescribeImageResponse>(
    functions,
    "describeImage",
  );
  const result = await callable({ path });
  return result.data.description;
}

export function subscribeJob(jobId: string, onUpdate: (job: JobWithId | null) => void): Unsubscribe {
  const jobRef = doc(db, "jobs", jobId);
  return onSnapshot(jobRef, (snapshot) => {
    if (!snapshot.exists()) {
      onUpdate(null);
      return;
    }
    onUpdate({ id: snapshot.id, ...(snapshot.data() as JobDoc) });
  });
}

export async function fetchJobById(jobId: string): Promise<JobWithId | null> {
  const jobRef = doc(db, "jobs", jobId);
  const snapshot = await getDoc(jobRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...(snapshot.data() as JobDoc) };
}

export function subscribeHistory(
  uid: string,
  pageSize: number,
  onUpdate: (jobs: JobWithId[], lastDoc: QueryDocumentSnapshot<DocumentData> | null) => void,
): Unsubscribe {
  const jobsRef = collection(db, "jobs");
  const q = query(
    jobsRef,
    where("ownerId", "==", uid),
    orderBy("createdAt", "desc"),
    limit(pageSize),
  );
  return onSnapshot(q, (snapshot) => {
    const jobs = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as JobDoc),
    }));
    const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    onUpdate(jobs, lastDoc);
  });
}

export async function fetchHistoryPage(
  uid: string,
  pageSize = 10,
  cursor?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<{ jobs: JobWithId[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
  const jobsRef = collection(db, "jobs");
  const baseQuery = query(
    jobsRef,
    where("ownerId", "==", uid),
    orderBy("createdAt", "desc"),
  );
  const pagedQuery = cursor
    ? query(baseQuery, startAfter(cursor), limit(pageSize))
    : query(baseQuery, limit(pageSize));

  const snapshot = await getDocs(pagedQuery);
  const jobs = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as JobDoc),
  }));

  const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
  return { jobs, lastDoc };
}

export async function getDownloadUrls(paths: string[]): Promise<string[]> {
  return Promise.all(paths.map((path) => getDownloadUrlCached(path)));
}

function getDownloadUrlCached(path: string): Promise<string> {
  const cached = downloadUrlCache.get(path);
  if (cached) return cached;
  const promise = getDownloadURL(ref(storage, path)).catch((error) => {
    downloadUrlCache.delete(path);
    throw error;
  });
  downloadUrlCache.set(path, promise);
  return promise;
}

function downloadEndpoint(path: string): string {
  const encoded = encodeURIComponent(path);
  const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
  if (useEmulators) {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
    const region = import.meta.env.VITE_FUNCTIONS_REGION || "us-central1";
    return `http://localhost:5001/${projectId}/${region}/downloadImage?path=${encoded}`;
  }
  return `/api/download?path=${encoded}`;
}

export async function downloadImageBlob(path: string): Promise<Blob> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Authentication required");
  }
  const token = await user.getIdToken();
  const response = await fetch(`${downloadEndpoint(path)}&ts=${Date.now()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.startsWith("image/")) {
    throw new Error(`Download failed: invalid content type ${contentType}`);
  }
  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error("Download failed: empty file");
  }
  return blob;
}
